import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEMO_SCENARIOS, getScenarioById, splitProfilesByStatus } from '../demo/demoData';
import type { DemoApiKey, DemoImageSettings, DemoProfile } from '../demo/types';
import ThresholdModal from './ThresholdModal';
import WebhookModal from './WebhookModal';
import ImageSettingsModal from './ImageSettingsModal';
import ModerationQueueSection, { ModerationQueueTab } from './ModerationQueueSection';
import ModerationReviewPanel from './ModerationReviewPanel';
import { useQueueProfileSelection } from '../hooks/useQueueProfileSelection';
import { logDemoAccess } from '../api/demo';
import './ModerationPage.css';
import './DemoPage.css';

const DEMO_PASSWORD = 'datemaroon';

const cloneProfiles = (profiles: DemoProfile[]): DemoProfile[] =>
  profiles.map((p) => ({ ...p, prompts: p.prompts?.map((q) => ({ ...q })), image_results: p.image_results?.map((r) => ({ ...r })) }));

const DemoPage: React.FC = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [scenarioId, setScenarioId] = useState(DEMO_SCENARIOS[0].id);
  const scenario = useMemo(() => getScenarioById(scenarioId), [scenarioId]);

  const [apiKeys, setApiKeys] = useState<DemoApiKey[]>(scenario.apiKeys);
  const [selectedApiKey, setSelectedApiKey] = useState(scenario.defaultApiKeyId);
  const [pendingProfiles, setPendingProfiles] = useState<DemoProfile[]>([]);
  const [approvedProfiles, setApprovedProfiles] = useState<DemoProfile[]>([]);
  const [rejectedProfiles, setRejectedProfiles] = useState<DemoProfile[]>([]);

  const [thresholdSettings, setThresholdSettings] = useState(scenario.thresholdSettings);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(scenario.webhookUrl);
  const [imageSettings, setImageSettings] = useState<DemoImageSettings>(scenario.imageSettings);

  const [activeTab, setActiveTab] = useState<'moderation' | 'config' | 'usage' | 'apiKeys'>('moderation');
  const [queueTab, setQueueTab] = useState<ModerationQueueTab>('pending');
  const { selectedProfileId, selectProfile } = useQueueProfileSelection(
    queueTab,
    pendingProfiles,
    approvedProfiles,
    rejectedProfiles
  );
  const [newApiKeyTitle, setNewApiKeyTitle] = useState('');
  const [newlyCreatedApiKey, setNewlyCreatedApiKey] = useState<string | null>(null);
  const [editingApiKeyId, setEditingApiKeyId] = useState<string | null>(null);
  const [editingApiKeyTitle, setEditingApiKeyTitle] = useState('');

  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [showImageSettingsModal, setShowImageSettingsModal] = useState(false);

  const loadProfilesForKey = useCallback(
    (keyId: string, sourceProfiles: DemoProfile[]) => {
      const split = splitProfilesByStatus(cloneProfiles(sourceProfiles), keyId);
      setPendingProfiles(split.pending);
      setApprovedProfiles(split.approved);
      setRejectedProfiles(split.rejected);
    },
    []
  );

  useEffect(() => {
    setApiKeys([...scenario.apiKeys]);
    setSelectedApiKey(scenario.defaultApiKeyId);
    setThresholdSettings({ ...scenario.thresholdSettings });
    setWebhookUrl(scenario.webhookUrl);
    setImageSettings({
      global_configuration: { ...scenario.imageSettings.global_configuration },
      per_image_configuration: { ...scenario.imageSettings.per_image_configuration },
      image_block: scenario.imageSettings.image_block,
    });
    loadProfilesForKey(scenario.defaultApiKeyId, scenario.profiles);
    setActiveTab('moderation');
  }, [scenario, loadProfilesForKey]);

  const findProfile = (profileId: string): DemoProfile | undefined => {
    return (
      pendingProfiles.find((p) => p.id === profileId) ||
      approvedProfiles.find((p) => p.id === profileId) ||
      rejectedProfiles.find((p) => p.id === profileId)
    );
  };

  const removeFromAll = (profileId: string) => {
    setPendingProfiles((prev) => prev.filter((p) => p.id !== profileId));
    setApprovedProfiles((prev) => prev.filter((p) => p.id !== profileId));
    setRejectedProfiles((prev) => prev.filter((p) => p.id !== profileId));
  };

  const handleStatusUpdate = (profileId: string, newStatus: DemoProfile['status']) => {
    const profile = findProfile(profileId);
    if (!profile) return;

    const updated: DemoProfile = { ...profile, status: newStatus };
    removeFromAll(profileId);

    if (newStatus === 'pending') setPendingProfiles((prev) => [updated, ...prev]);
    if (newStatus === 'approved') setApprovedProfiles((prev) => [updated, ...prev]);
    if (newStatus === 'rejected') setRejectedProfiles((prev) => [updated, ...prev]);
  };

  const handleDeleteProfile = (profileId: string) => {
    const confirmed = window.confirm('Remove this demo profile from the queue?');
    if (!confirmed) return;
    removeFromAll(profileId);
  };

  const totalProfiles = pendingProfiles.length + rejectedProfiles.length + approvedProfiles.length;
  const averageScore = useMemo(() => {
    if (!totalProfiles) return 0;
    const all = [...pendingProfiles, ...rejectedProfiles, ...approvedProfiles];
    return Math.round(all.reduce((sum, profile) => sum + profile.trust_score, 0) / all.length);
  }, [approvedProfiles, pendingProfiles, rejectedProfiles, totalProfiles]);

  const handleCreateApiKey = () => {
    const title = newApiKeyTitle.trim() || 'Untitled Key';
    const id = `demo-key-${Date.now()}`;
    const prefix = `nv_demo_••••${Math.random().toString(36).slice(2, 6)}`;
    setApiKeys((prev) => [...prev, { id, title, key_prefix: prefix }]);
    setNewlyCreatedApiKey(`${prefix}_demo_secret_key_only_shown_once`);
    setNewApiKeyTitle('');
    setSelectedApiKey(id);
    setActiveTab('apiKeys');
  };

  const handleDeleteApiKey = (keyId: string) => {
    if (apiKeys.length <= 1) {
      alert('Keep at least one demo API key.');
      return;
    }
    const confirmed = window.confirm('Delete this demo API key?');
    if (!confirmed) return;
    setPendingProfiles((prev) => prev.filter((p) => p.api_key_id !== keyId));
    setApprovedProfiles((prev) => prev.filter((p) => p.api_key_id !== keyId));
    setRejectedProfiles((prev) => prev.filter((p) => p.api_key_id !== keyId));
    setApiKeys((prev) => {
      const next = prev.filter((k) => k.id !== keyId);
      if (selectedApiKey === keyId) setSelectedApiKey(next[0]?.id ?? '');
      return next;
    });
  };

  const saveApiKeyTitle = (keyId: string) => {
    const trimmed = editingApiKeyTitle.trim();
    if (!trimmed) return;
    setApiKeys((prev) => prev.map((k) => (k.id === keyId ? { ...k, title: trimmed } : k)));
    setEditingApiKeyId(null);
    setEditingApiKeyTitle('');
  };

  const resetScenario = () => {
    const fresh = getScenarioById(scenarioId);
    setApiKeys([...fresh.apiKeys]);
    setSelectedApiKey(fresh.defaultApiKeyId);
    setThresholdSettings({ ...fresh.thresholdSettings });
    setWebhookUrl(fresh.webhookUrl);
    setImageSettings({
      global_configuration: { ...fresh.imageSettings.global_configuration },
      per_image_configuration: { ...fresh.imageSettings.per_image_configuration },
      image_block: fresh.imageSettings.image_block,
    });
    loadProfilesForKey(fresh.defaultApiKeyId, fresh.profiles);
  };

  const renderReviewPanel = (profiles: DemoProfile[], emptyMessage: string) => (
    <ModerationReviewPanel
      profiles={profiles}
      emptyMessage={emptyMessage}
      selectedProfileId={selectedProfileId}
      onSelectProfile={selectProfile}
      onApprove={(id) => handleStatusUpdate(id, 'approved')}
      onReject={(id) => handleStatusUpdate(id, 'rejected')}
      onMoveToPending={(id) => handleStatusUpdate(id, 'pending')}
      onDelete={handleDeleteProfile}
    />
  );

  const renderModerationView = () => (
    <div className="tab-panel">
      <section className="stats-grid">
        <article className="stats-card">
          <p>Incoming queue</p>
          <h3>{pendingProfiles.length}</h3>
        </article>
        <article className="stats-card">
          <p>Total users</p>
          <h3>{totalProfiles}</h3>
        </article>
        <article className="stats-card">
          <p>Average trust score</p>
          <h3>{averageScore}</h3>
        </article>
        <article className="stats-card stats-card--webhook">
          <p>Webhook</p>
          {webhookUrl ? (
            <code className="stats-webhook-url" title={webhookUrl}>
              {webhookUrl}
            </code>
          ) : (
            <h3>Not configured</h3>
          )}
        </article>
      </section>

      <ModerationQueueSection
        queueTab={queueTab}
        onQueueTabChange={setQueueTab}
        pendingCount={pendingProfiles.length}
        approvedCount={approvedProfiles.length}
        rejectedCount={rejectedProfiles.length}
        hint={<p className="demo-scenario-hint">{scenario.description}</p>}
        pendingContent={renderReviewPanel(pendingProfiles, 'No incoming users right now.')}
        approvedContent={renderReviewPanel(approvedProfiles, 'No approved users.')}
        rejectedContent={renderReviewPanel(rejectedProfiles, 'No rejected users.')}
      />
    </div>
  );

  const renderConfigView = () => (
    <div className="tab-panel">
      <section className="config-grid">
        <article className="config-card">
          <p className="config-kicker">Threshold controls</p>
          <h2>Moderation confidence bands</h2>
          <div className="threshold-readout">
            <div>
              <span>Approve automatically</span>
              <strong>&ge; {thresholdSettings.auto_approve_threshold}</strong>
            </div>
            <div>
              <span>Manual review</span>
              <strong>
                {thresholdSettings.auto_reject_threshold} - {thresholdSettings.auto_approve_threshold - 1}
              </strong>
            </div>
            <div>
              <span>Reject automatically</span>
              <strong>&lt; {thresholdSettings.auto_reject_threshold}</strong>
            </div>
          </div>
          <div className="config-actions">
            <button type="button" className="pill-button primary" onClick={() => setShowThresholdModal(true)}>
              Open threshold controls
            </button>
            <button type="button" className="pill-button" onClick={resetScenario}>
              Reset scenario data
            </button>
          </div>
        </article>

        <article className="config-card">
          <p className="config-kicker">Image policy</p>
          <h2>Optional image verification rules</h2>
          <p className="subtext">
            Face detection and AI-image checks (demo only — changes stay in your browser session).
          </p>
          <div className="config-actions">
            <button type="button" className="pill-button" onClick={() => setShowImageSettingsModal(true)}>
              Edit image policy
            </button>
          </div>
        </article>
      </section>
    </div>
  );

  const renderUsageView = () => (
    <div className="tab-panel">
      <section className="usage-grid">
        <article className="usage-card">
          <p className="config-kicker">Webhooks</p>
          <h2>Delivery and integration docs</h2>
          <p className="subtext">
            Send moderation decisions to your backend when profiles are approved, rejected, or returned to pending.
          </p>
          <pre className="docs-snippet">
{`POST /webhook-endpoint
{
  "profile_id": "string",
  "status": "approved | rejected | pending",
  "trust_score": 0
}`}
          </pre>
          <p className="subtext">Current endpoint: {webhookUrl || 'Not configured'}</p>
          <div className="config-actions">
            <button type="button" className="pill-button primary" onClick={() => setShowWebhookModal(true)}>
              Configure webhook URL
            </button>
          </div>
        </article>
      </section>
    </div>
  );

  const renderApiKeysView = () => (
    <div className="tab-panel">
      <section className="api-keys-grid">
        <article className="config-card">
          <p className="config-kicker">Create key</p>
          <h2>Generate a new API key</h2>
          <p className="subtext">Demo keys are stored locally and are not sent to any server.</p>
          <div className="api-key-create-row">
            <input
              className="api-key-title-input"
              type="text"
              placeholder="Key title (optional)"
              value={newApiKeyTitle}
              onChange={(e) => setNewApiKeyTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateApiKey();
              }}
            />
            <button type="button" className="pill-button primary" onClick={handleCreateApiKey}>
              Create key
            </button>
          </div>
          {!!newlyCreatedApiKey && (
            <div className="new-api-key-box">
              <p>Copy this key now. It will only be shown once (demo).</p>
              <code>{newlyCreatedApiKey}</code>
              <div className="config-actions">
                <button type="button" className="pill-button" onClick={() => navigator.clipboard.writeText(newlyCreatedApiKey)}>
                  Copy key
                </button>
                <button type="button" className="pill-button" onClick={() => setNewlyCreatedApiKey(null)}>
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </article>

        <article className="config-card">
          <p className="config-kicker">Manage keys</p>
          <h2>Active API keys</h2>
          {apiKeys.length === 0 ? (
            <div className="empty-state">No API keys yet.</div>
          ) : (
            <div className="api-key-list">
              {apiKeys.map((key) => (
                <div key={key.id} className="api-key-list-item">
                  <div className="api-key-list-main">
                    {editingApiKeyId === key.id ? (
                      <div className="api-key-edit-row">
                        <input
                          className="api-key-title-input"
                          type="text"
                          value={editingApiKeyTitle}
                          onChange={(e) => setEditingApiKeyTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveApiKeyTitle(key.id);
                            if (e.key === 'Escape') {
                              setEditingApiKeyId(null);
                              setEditingApiKeyTitle('');
                            }
                          }}
                        />
                        <button type="button" className="pill-button" onClick={() => saveApiKeyTitle(key.id)} disabled={!editingApiKeyTitle.trim()}>
                          Save
                        </button>
                      </div>
                    ) : (
                      <>
                        <strong>{key.title || 'Untitled Key'}</strong>
                        <p>{key.key_prefix}</p>
                      </>
                    )}
                  </div>
                  <div className="api-key-actions">
                    {editingApiKeyId !== key.id && (
                      <button
                        type="button"
                        className="pill-button"
                        onClick={() => {
                          setEditingApiKeyId(key.id);
                          setEditingApiKeyTitle(key.title || 'Untitled Key');
                        }}
                      >
                        Rename
                      </button>
                    )}
                    <button type="button" className="pill-button" onClick={() => handleDeleteApiKey(key.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
      </section>
    </div>
  );

  const handleUnlock = (event: React.FormEvent) => {
    event.preventDefault();
    if (password === DEMO_PASSWORD) {
      setUnlocked(true);
      setPasswordError('');
      setPassword('');
      logDemoAccess().catch((err) => console.error('Failed to log demo access:', err));
      return;
    }
    setPasswordError('Incorrect password.');
  };

  if (!unlocked) {
    return (
      <div className="demo-lock-screen">
        <div className="demo-lock-inner">
          <h1 className="demo-lock-title">Demo</h1>
          <form className="demo-lock-form" onSubmit={handleUnlock}>
            <input
              className="demo-lock-input"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError('');
              }}
              placeholder="Password"
              autoComplete="current-password"
              autoFocus
            />
            {passwordError && <p className="demo-lock-error">{passwordError}</p>}
            <button type="submit" className="demo-lock-submit">
              Enter
            </button>
          </form>
          <Link to="/" className="demo-lock-back">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="demo-page">
      <header className="demo-top-bar">
        <p>
          <strong>Interactive demo</strong> — approve, reject, and explore moderation flows with sample data. Nothing is saved.
        </p>
        <div className="demo-top-actions">
          <select
            className="demo-scenario-select"
            value={scenarioId}
            onChange={(e) => setScenarioId(e.target.value)}
            aria-label="Demo scenario"
          >
            {DEMO_SCENARIOS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          <Link to="/" className="demo-home-link">
            Back to home
          </Link>
        </div>
      </header>

      <div className="moderation-dashboard demo-dashboard">
        <section className="dashboard-shell">
          <aside className="dashboard-nav" aria-label="Dashboard navigation">
            <div className="dashboard-nav-brand">
              <p className="nav-kicker">Dashboard</p>
              <h1 className="nav-title">Date Maroont</h1>
            </div>
            <nav className="dashboard-nav-links">
              <button type="button" className={`nav-tab ${activeTab === 'moderation' ? 'active' : ''}`} onClick={() => setActiveTab('moderation')}>
                Moderation
              </button>
              <button type="button" className={`nav-tab ${activeTab === 'config' ? 'active' : ''}`} onClick={() => setActiveTab('config')}>
                Config
              </button>
              <button type="button" className={`nav-tab ${activeTab === 'usage' ? 'active' : ''}`} onClick={() => setActiveTab('usage')}>
                Usage
              </button>
              <button type="button" className={`nav-tab ${activeTab === 'apiKeys' ? 'active' : ''}`} onClick={() => setActiveTab('apiKeys')}>
                API Key
              </button>
            </nav>
          </aside>

          <main className="dashboard-main">
            <section className="dashboard-head">
              <div>
                <p className="head-kicker">Moderation (Demo)</p>
              </div>
              <div className="head-actions">
                <select
                  className="api-key-selector"
                  value={selectedApiKey}
                  onChange={(e) => {
                    const keyId = e.target.value;
                    setSelectedApiKey(keyId);
                    loadProfilesForKey(keyId, scenario.profiles);
                  }}
                >
                  {apiKeys.map((key) => (
                    <option key={key.id} value={key.id}>
                      {key.title || key.key_prefix}
                    </option>
                  ))}
                </select>
                <button type="button" className="pill-button" onClick={resetScenario}>
                  Reset queue
                </button>
              </div>
            </section>

            {activeTab === 'moderation' && renderModerationView()}
            {activeTab === 'config' && renderConfigView()}
            {activeTab === 'usage' && renderUsageView()}
            {activeTab === 'apiKeys' && renderApiKeysView()}
          </main>
        </section>

        {showThresholdModal && (
          <ThresholdModal
            autoApproveThreshold={thresholdSettings.auto_approve_threshold}
            autoRejectThreshold={thresholdSettings.auto_reject_threshold}
            onSave={(autoApprove, autoReject) => {
              setThresholdSettings({ auto_approve_threshold: autoApprove, auto_reject_threshold: autoReject });
              setShowThresholdModal(false);
            }}
            onClose={() => setShowThresholdModal(false)}
          />
        )}
        {showWebhookModal && (
          <WebhookModal
            webhookUrl={webhookUrl || ''}
            onSave={(url) => {
              setWebhookUrl(url);
              setShowWebhookModal(false);
            }}
            onClose={() => setShowWebhookModal(false)}
          />
        )}
        {showImageSettingsModal && (
          <ImageSettingsModal
            globalConfig={imageSettings.global_configuration}
            perImageConfig={imageSettings.per_image_configuration}
            imageBlock={imageSettings.image_block}
            onSave={(globalConfig, perImageConfig, imageBlock) => {
              setImageSettings({
                global_configuration: globalConfig,
                per_image_configuration: perImageConfig,
                image_block: imageBlock,
              });
              setShowImageSettingsModal(false);
            }}
            onClose={() => setShowImageSettingsModal(false)}
          />
        )}

      </div>
    </div>
  );
};

export default DemoPage;
